import { supabase } from './supabaseService';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

// `companies` retirado 2026-09-18: la tabla se fusionó con `contacts`
// (entity_type='company'). Cualquier evento sobre empresas llega ya por
// el canal de contacts.
type TableName = 'contacts' | 'leads' | 'meetings' | 'activities' | 'users';
type ChangeType = 'INSERT' | 'UPDATE' | 'DELETE';

interface RealtimeEvent {
  table: TableName;
  type: ChangeType;
  record: any;
  old?: any;
  timestamp: number;
}

type RealtimeCallback = (event: RealtimeEvent) => void;

class RealtimeSyncService {
  private channels: Map<TableName, RealtimeChannel> = new Map();
  private listeners: Map<TableName, Set<RealtimeCallback>> = new Map();
  private reconnectAttempts: Map<TableName, number> = new Map();
  private maxReconnectAttempts = 5;
  private isMonitoring = false;
  private lastEventTimestamp: Map<TableName, number> = new Map();
  private rateLimitWindow = 1000;

  startMonitoring(tables: TableName[] = ['contacts', 'leads', 'companies', 'meetings', 'activities', 'users']) {
    if (this.isMonitoring) {
      logger.debug('Realtime monitoring already active');
      return;
    }

    console.log('🚀 Starting Realtime monitoring for tables:', tables);
    this.isMonitoring = true;

    tables.forEach(table => {
      this.subscribeToTable(table);
    });
  }

  stopMonitoring() {
    if (!this.isMonitoring) return;

    console.log('🛑 Stopping Realtime monitoring');
    this.isMonitoring = false;

    this.channels.forEach((channel, table) => {
      logger.debug(`Unsubscribing from ${table}`);
      supabase.removeChannel(channel);
    });

    this.channels.clear();
    this.listeners.clear();
    this.reconnectAttempts.clear();
    this.lastEventTimestamp.clear();
  }

  subscribe(table: TableName, callback: RealtimeCallback): () => void {
    if (!this.listeners.has(table)) {
      this.listeners.set(table, new Set());
    }

    this.listeners.get(table)!.add(callback);
    logger.debug(`Subscribed to ${table} changes (${this.listeners.get(table)!.size} listeners)`);

    if (this.isMonitoring && !this.channels.has(table)) {
      this.subscribeToTable(table);
    }

    return () => {
      const listeners = this.listeners.get(table);
      if (listeners) {
        listeners.delete(callback);
        logger.debug(`Unsubscribed from ${table} changes (${listeners.size} listeners remaining)`);

        if (listeners.size === 0) {
          this.listeners.delete(table);
          const channel = this.channels.get(table);
          if (channel) {
            logger.debug(`No more listeners for ${table}, removing channel`);
            supabase.removeChannel(channel);
            this.channels.delete(table);
          }
        }
      }
    };
  }

  private subscribeToTable(table: TableName) {
    if (this.channels.has(table)) {
      console.log(`⏭️ Already subscribed to ${table}`);
      return;
    }

    logger.debug(`Subscribing to ${table} table...`);

    const channel = supabase
      .channel(`public:${table}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table
        },
        (payload: RealtimePostgresChangesPayload<any>) => {
          this.handleRealtimeEvent(table, payload);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.info(`Subscribed to ${table} successfully`);
          this.reconnectAttempts.set(table, 0);
        } else if (status === 'CHANNEL_ERROR') {
          logger.error(`Error subscribing to ${table}`);
          this.handleSubscriptionError(table);
        } else if (status === 'TIMED_OUT') {
          console.error(`⏱️ Subscription to ${table} timed out`);
          this.handleSubscriptionError(table);
        } else {
          console.log(`📡 ${table} subscription status:`, status);
        }
      });

    this.channels.set(table, channel);
  }

  private handleRealtimeEvent(table: TableName, payload: RealtimePostgresChangesPayload<any>) {
    const now = Date.now();
    const lastEvent = this.lastEventTimestamp.get(table) || 0;

    if (now - lastEvent < this.rateLimitWindow) {
      console.log(`⏭️ Rate limiting ${table} event (too soon since last event)`);
      return;
    }

    this.lastEventTimestamp.set(table, now);

    const event: RealtimeEvent = {
      table,
      type: payload.eventType as ChangeType,
      record: payload.new,
      old: payload.old,
      timestamp: now
    };

    console.log(`🔔 Realtime event from ${table}:`, {
      type: event.type,
      recordId: event.record?.id,
      timestamp: new Date(event.timestamp).toISOString()
    });

    const listeners = this.listeners.get(table);
    if (listeners && listeners.size > 0) {
      listeners.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          logger.error(`Error in ${table} listener:`, error);
        }
      });
    }

    const customEvent = new CustomEvent('realtime-sync', { detail: event });
    window.dispatchEvent(customEvent);
  }

  private handleSubscriptionError(table: TableName) {
    const attempts = this.reconnectAttempts.get(table) || 0;

    if (attempts >= this.maxReconnectAttempts) {
      logger.error(`Max reconnection attempts reached for ${table}`);
      return;
    }

    this.reconnectAttempts.set(table, attempts + 1);

    const delay = Math.min(1000 * Math.pow(2, attempts), 30000);
    console.log(`⏳ Reconnecting to ${table} in ${delay}ms (attempt ${attempts + 1}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      const channel = this.channels.get(table);
      if (channel) {
        supabase.removeChannel(channel);
        this.channels.delete(table);
      }

      this.subscribeToTable(table);
    }, delay);
  }

  getConnectionStatus(): { [key in TableName]?: 'connected' | 'disconnected' | 'reconnecting' } {
    const status: { [key in TableName]?: 'connected' | 'disconnected' | 'reconnecting' } = {};

    this.channels.forEach((channel, table) => {
      const state = channel.state;

      if (state === 'joined') {
        status[table] = 'connected';
      } else if (state === 'closed' || state === 'errored') {
        const attempts = this.reconnectAttempts.get(table) || 0;
        status[table] = attempts > 0 ? 'reconnecting' : 'disconnected';
      } else {
        status[table] = 'reconnecting';
      }
    });

    return status;
  }

  isConnected(table?: TableName): boolean {
    if (table) {
      const channel = this.channels.get(table);
      return channel?.state === 'joined';
    }

    return Array.from(this.channels.values()).some(channel => channel.state === 'joined');
  }
}

export const realtimeSyncService = new RealtimeSyncService();
